import { companyTodayIsoDate } from '@/lib/company-date'

const COMPANY_IDS_PARAM = 'company_ids[]'
const PRIOR_PARAM = 'prior'
const SORT_PARAM = 'sort'

export type GroupReportCompanySelection =
  | { readonly kind: 'all' }
  | { readonly kind: 'explicit'; readonly ids: readonly string[] }

export type GroupReportPrior = '0' | '1'
export type GroupReportSort = 'net_result_desc' | 'net_result_asc'
export type GroupReportIsoDate = string

/**
 * The single normalized search shape shared by group-report routes and API
 * requests. An explicit empty selection is intentionally different from `all`.
 */
export interface GroupReportSearch {
  readonly selection: GroupReportCompanySelection
  readonly from: GroupReportIsoDate | undefined
  readonly as_at: GroupReportIsoDate | undefined
  readonly prior: GroupReportPrior
  readonly sort: GroupReportSort
}

export type GroupReportSearchInput = Record<string, unknown> | URLSearchParams

export function todayIso(): string {
  return companyTodayIsoDate()
}

const DEFAULT_PRIOR: GroupReportPrior = '0'
const DEFAULT_SORT: GroupReportSort = 'net_result_desc'

/**
 * Parse route search values without using the browser's Date parser. Invalid
 * values are omitted so serializers can never emit a non-ISO date; the API
 * remains responsible for reporting a rejected request when appropriate.
 */
export function groupReportSearchFrom(input: GroupReportSearchInput): GroupReportSearch {
  const companyIds = readRepeated(input, COMPANY_IDS_PARAM)
  const ids = companyIds.values.filter((id) => id !== '')

  return {
    selection: companyIds.present ? { kind: 'explicit', ids: uniqueSorted(ids) } : { kind: 'all' },
    from: parseIsoDate(readScalar(input, 'from')),
    as_at: parseIsoDate(readScalar(input, 'as_at')),
    prior: parsePrior(readScalar(input, PRIOR_PARAM)),
    sort: parseSort(readScalar(input, SORT_PARAM)),
  }
}

/** Serialize all URL-backed state, including the overview-only controls. */
export function serializeGroupReportRouteSearch(search: GroupReportSearch): URLSearchParams {
  const params = serializeGroupReportApiFilters(search)
  params.set(PRIOR_PARAM, isPrior(search.prior) ? search.prior : DEFAULT_PRIOR)
  params.set(SORT_PARAM, isSort(search.sort) ? search.sort : DEFAULT_SORT)
  return params
}

/**
 * Serialize only the filters understood by the group-report API. In
 * particular, `prior` and `sort` are presentation state and must not leak into
 * API requests.
 */
export function serializeGroupReportApiFilters(search: GroupReportSearch): URLSearchParams {
  const params = new URLSearchParams()

  if (search.selection.kind === 'explicit') {
    const ids = uniqueSorted(search.selection.ids.filter((id) => id !== ''))
    if (ids.length === 0) {
      // An empty value preserves the distinction between an omitted parameter
      // and an explicitly empty company_ids[] selection.
      params.append(COMPANY_IDS_PARAM, '')
    } else {
      for (const id of ids) params.append(COMPANY_IDS_PARAM, id)
    }
  }

  if (isIsoDate(search.from)) params.set('from', search.from)
  if (isIsoDate(search.as_at)) params.set('as_at', search.as_at)

  return params
}

/** Serialize drill-down line IDs using the backend's repeated bracket name. */
export function serializeGroupReportDrillDownIds(ids: readonly string[]): URLSearchParams {
  const params = new URLSearchParams()
  for (const id of ids) params.append('ids[]', id)
  return params
}

/** A deterministic projection suitable for query keys and stable links. */
export function groupReportSearchKey(search: GroupReportSearch): string {
  return serializeGroupReportRouteSearch(search).toString()
}

function readRepeated(
  input: GroupReportSearchInput,
  key: string,
): { present: boolean; values: string[] } {
  if (input instanceof URLSearchParams) {
    return { present: input.has(key), values: input.getAll(key) }
  }

  if (!Object.hasOwn(input, key)) {
    return { present: false, values: [] }
  }

  const value = input[key]
  if (Array.isArray(value)) {
    return {
      present: true,
      values: value.filter((item): item is string => typeof item === 'string'),
    }
  }

  return { present: true, values: typeof value === 'string' ? [value] : [] }
}

function readScalar(input: GroupReportSearchInput, key: string): unknown {
  if (input instanceof URLSearchParams) return input.get(key) ?? undefined

  const value = input[key]
  if (Array.isArray(value)) return value.length === 1 ? value[0] : undefined
  return value
}

function parseIsoDate(value: unknown): GroupReportIsoDate | undefined {
  return isIsoDate(value) ? value : undefined
}

/** Strict YYYY-MM-DD validation, including the calendar day range. */
export function isIsoDate(value: unknown): value is GroupReportIsoDate {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false

  const year = numericPart(value, 0, 4)
  const month = numericPart(value, 5, 7)
  const day = numericPart(value, 8, 10)
  if (year === 0 || month < 1 || month > 12) return false

  const daysInMonth = [31, isLeapYear(year) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]
  const maxDay = daysInMonth[month - 1]
  return maxDay !== undefined && day >= 1 && day <= maxDay
}

function numericPart(value: string, start: number, end: number): number {
  return Number(value.slice(start, end))
}

function isLeapYear(year: number): boolean {
  return year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0)
}

function parsePrior(value: unknown): GroupReportPrior {
  return isPrior(value) ? value : DEFAULT_PRIOR
}

function isPrior(value: unknown): value is GroupReportPrior {
  return value === '0' || value === '1'
}

function parseSort(value: unknown): GroupReportSort {
  return isSort(value) ? value : DEFAULT_SORT
}

function isSort(value: unknown): value is GroupReportSort {
  return value === 'net_result_desc' || value === 'net_result_asc'
}

function uniqueSorted(values: readonly string[]): string[] {
  return [...new Set(values)].sort(compareStrings)
}

function compareStrings(left: string, right: string): number {
  if (left === right) return 0
  return left < right ? -1 : 1
}
